// filepath: apps/web/src/features/directory/locations/LocationsPage.tsx
//
// FR-UI-DIR-1 AC-6 — locations catalog page.
//
// ISS-515 (BA option a): the SPA Location interface was aligned to
// the Go wire shape — the four phantom address fields
// (country_code / city / address_line / country) and the two
// denormalized helpers (kind / updated_at) are gone. This page
// renders the Go-aligned columns only: Name / Code / Parent /
// Status / Created. The silent-empty list bug (BA note_17803)
// is now structurally impossible (the Go wire has no fields that
// return undefined for the SPA to coerce with `?? ""`).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDatePattern } from "@/i18n/format";
import { Card } from "@/design-system/Card";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { navigate } from "@/features/cabinet/usePathname";
import { useLocations } from "../queries";
import { emptyLocationsFilter } from "../types";

/**
 * ISS-431-uatrzt2 React crash fix — the project explicitly opts
 * out of `react-router-dom` (FR-UI-CABINET-1 AC-9) and uses its
 * own `navigate()` primitive. The previous `<Link>` import from
 * `react-router-dom` threw on every render.
 */
function handleSpaNavigate(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
): void {
  if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return;
  }
  e.preventDefault();
  navigate(href);
}

/**
 * Short-id helper for the parent column. The list endpoint does
 * NOT denormalize the parent name (per Go's
 * `internal/directory/location/handlers.go:289`
 * `WriteStrippedList(..., "records")` shape) — N+1 name lookups
 * would belong on the detail page, not the list. Until ISS-518 or a
 * future FR adds a `{records, ...}` envelope with a denormalized
 * `parent_name`, the list shows the short id for a child location
 * and `Root` for a parent itself.
 */
function shortId(uuid: string | null): string {
  if (!uuid) return "Root";
  return uuid.slice(0, 8);
}

export function LocationsPage(): React.ReactElement {
  const { t, locale } = useTranslation();
  const filter = emptyLocationsFilter();
  const query = useLocations(filter);

  switch (query.state) {
    case "loading":
      return (
        <p
          className="directory-locations__loading"
          data-testid="locations-loading"
        >
          {t("directory.locations.state.loading")}
        </p>
      );
    case "permission-denied":
      return (
        <p
          role="alert"
          className="directory-locations__permission-denied"
          data-testid="locations-permission-denied"
        >
          {t("directory.locations.state.permission_denied")}
        </p>
      );
    case "validation-error":
      return (
        <p
          role="alert"
          className="directory-locations__validation-error"
          data-testid="locations-validation-error"
        >
          {query.error?.message ??
            t("directory.locations.state.validation_error")}
        </p>
      );
    case "fetch-failure":
      return (
        <p
          role="alert"
          className="directory-locations__fetch-failure"
          data-testid="locations-fetch-failure"
        >
          {t("directory.locations.state.fetch_failure")}
        </p>
      );
    case "rate-limit":
      return (
        <p
          role="alert"
          className="directory-locations__rate-limit"
          data-testid="locations-rate-limit"
        >
          {t("directory.locations.state.rate_limit")}
        </p>
      );
    case "success": {
      const data = query.data;
      // ISS-515 BA option (a): Go emits `{records, total}` per
      // `authorization.WriteStrippedList(..., "records")`; the
      // SPA's typed DirectoryListResponse uses `items` (counter-
      // parts / people use a different envelope). The list hook
      // normalises this — see `useLocationsList` in queries.ts.
      // If the hook returns nothing (empty list), fall through
      // to the empty state.
      const records = (data as unknown as { records?: unknown[] })?.records;
      const entries = Array.isArray(records) ? records : [];
      return (
        <section className="directory-locations" data-testid="locations-page">
          <header className="directory-locations__header">
            <h1 id="locations-title">
              {t("directory.locations.title", "Locations")}
            </h1>
            <a
              href="/admin/locations/new"
              data-testid="locations-new-link"
              onClick={(e) => handleSpaNavigate(e, "/admin/locations/new")}
              style={{ textDecoration: "none" }} // design-adherence-ignore: link reset (button-styled anchor; no underline)
            >
              <Button variant="primary">
                {t("directory.locations.list.new", "New location")}
              </Button>
            </a>
          </header>
          {entries.length === 0 ? (
            <Card>
              <p
                className="directory-locations__empty"
                data-testid="locations-empty"
              >
                {t(
                  "directory.locations.list.empty",
                  "No locations match the current filter.",
                )}
              </p>
            </Card>
          ) : (
            <Card padding="sm">
              <table
                aria-labelledby="locations-title"
                className="directory-locations__table"
              >
                <thead>
                  <tr>
                    <th scope="col">
                      {t("directory.locations.column.name", "Name")}
                    </th>
                    <th scope="col">
                      {t("directory.locations.column.code", "Code")}
                    </th>
                    <th scope="col">
                      {t("directory.locations.column.parent", "Parent")}
                    </th>
                    <th scope="col">
                      {t("directory.locations.column.status", "Status")}
                    </th>
                    <th scope="col">
                      {t("directory.locations.column.created_at", "Created")}
                    </th>
                    <th scope="col">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((l) => {
                    // `l` is typed as `unknown` only because the
                    // hook's `DirectoryListResponse<Location>` carries
                    // an `items` key the Go envelope does NOT have;
                    // we narrowed above to the actual records array.
                    // Locally assert the Location shape for the row
                    // renderer (compile-time drift guard mirrors the
                    // fixture's compile-time guard).
                    const row = l as import("../types").Location;
                    return (
                      <tr
                        key={row.id}
                        data-testid={`location-row-${row.id}`}
                        data-location-id={row.id}
                      >
                        <td>
                          <a
                            href={`/admin/locations/${encodeURIComponent(row.id)}`}
                            data-testid={`location-row-${row.id}-detail`}
                            onClick={(e) =>
                              handleSpaNavigate(
                                e,
                                `/admin/locations/${encodeURIComponent(row.id)}`,
                              )
                            }
                          >
                            {row.name}
                          </a>
                        </td>
                        <td>{row.code ?? "—"}</td>
                        <td>{shortId(row.parent_location_id)}</td>
                        <td>
                          <Badge
                            tone={
                              row.status === "active" ? "success" : "neutral"
                            }
                          >
                            {t(
                              `directory.locations.status.${row.status}`,
                              row.status,
                            )}
                          </Badge>
                        </td>
                        <td>
                          {formatDatePattern(new Date(row.created_at), locale)}
                        </td>
                        <td>
                          <a
                            href={`/admin/locations/${encodeURIComponent(row.id)}/edit`}
                            data-testid={`location-row-${row.id}-edit`}
                            onClick={(e) =>
                              handleSpaNavigate(
                                e,
                                `/admin/locations/${encodeURIComponent(row.id)}/edit`,
                              )
                            }
                          >
                            {t("directory.locations.list.edit", "Edit")}
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </section>
      );
    }
  }
}
